import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  message,
  Modal,
  Card,
  Tooltip,
  Form,
  InputNumber,
  Spin,
  Divider,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  CreditCardOutlined,
  DeleteOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import PersianDatePicker from '../../components/PersianDatePicker';
import { pettyCashApi, bankAccountsApi, expensesApi, expenseCategoriesApi, extractApiUserMessage } from '../../utils/api';
import { financialApi } from '../../utils/financialApi';
import { TableSkeleton } from '../../components/SkeletonLoaders';
import { formatMoneyFa } from '../../utils/formatPriceToman';
import { toGregorian } from '../../utils/dateUtils';

const { Option } = Select;

const amountToPersianWords = (n) => {
  if (!n || isNaN(n)) return '';
  n = Math.floor(Number(n));
  if (n === 0) return 'صفر تومان';
  const units = ['', 'یک', 'دو', 'سه', 'چهار', 'پنج', 'شش', 'هفت', 'هشت', 'نه'];
  const teens = ['ده', 'یازده', 'دوازده', 'سیزده', 'چهارده', 'پانزده', 'شانزده', 'هفده', 'هجده', 'نوزده'];
  const tens = ['', 'ده', 'بیست', 'سی', 'چهل', 'پنجاه', 'شصت', 'هفتاد', 'هشتاد', 'نود'];
  const hundreds = ['', 'یکصد', 'دویست', 'سیصد', 'چهارصد', 'پانصد', 'ششصد', 'هفتصد', 'هشتصد', 'نهصد'];
  const scales = ['', 'هزار', 'میلیون', 'میلیارد', 'تریلیون'];

  function chunkToWords(num) {
    let parts = [];
    const h = Math.floor(num / 100);
    const rem = num % 100;
    if (h > 0) parts.push(hundreds[h]);
    if (rem >= 10 && rem <= 19) {
      parts.push(teens[rem - 10]);
    } else {
      const t = Math.floor(rem / 10);
      const u = rem % 10;
      if (t > 0) parts.push(tens[t]);
      if (u > 0) parts.push(units[u]);
    }
    return parts.join(' و ');
  }

  let chunks = [];
  let temp = n;
  while (temp > 0) {
    chunks.push(temp % 1000);
    temp = Math.floor(temp / 1000);
  }

  let resParts = [];
  for (let i = chunks.length - 1; i >= 0; i--) {
    const chunk = chunks[i];
    if (chunk > 0) {
      const words = chunkToWords(chunk);
      const scale = scales[i];
      resParts.push(scale ? words + ' ' + scale : words);
    }
  }
  return resParts.join(' و ') + ' تومان';
};

const PettyCash = () => {
  const [loading, setLoading] = useState(false);
  const [pettyCashes, setPettyCashes] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  
  const [isCategoryModalVisible, setIsCategoryModalVisible] = useState(false);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [categorySubmitting, setCategorySubmitting] = useState(false);
  const [deletingCategoryId, setDeletingCategoryId] = useState(null);
  const [quickCategoryName, setQuickCategoryName] = useState('');

  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isSpendModalVisible, setIsSpendModalVisible] = useState(false);
  const [selectedPettyCash, setSelectedPettyCash] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const [createAmount, setCreateAmount] = useState(null);
  const [spendAmount, setSpendAmount] = useState(null);
  
  const [createForm] = Form.useForm();
  const [spendForm] = Form.useForm();
  const [categoryForm] = Form.useForm();

  const fetchPettyCashes = async () => {
    setLoading(true);
    try {
      const res = await pettyCashApi.getAll();
      if (res?.data?.petty_cashes) {
        setPettyCashes(Array.isArray(res.data.petty_cashes) ? res.data.petty_cashes : []);
      } else if (res?.petty_cashes) {
        setPettyCashes(Array.isArray(res.petty_cashes) ? res.petty_cashes : []);
      } else if (Array.isArray(res?.data)) {
        setPettyCashes(Array.isArray(res.data) ? res.data : []);
      }
    } catch (error) {
      console.error('Error fetching petty cash:', error);
      message.error('خطا در دریافت لیست تنخواه‌گردان');
    } finally {
      setLoading(false);
    }
  };

  const fetchDependencies = async () => {
    try {
      const [bankRes, createRes, financialRes] = await Promise.all([
        bankAccountsApi.getAll().catch(err => {
          console.warn('bankAccountsApi.getAll error:', err);
          return null;
        }),
        expensesApi.getCreateData().catch(err => {
          console.warn('expensesApi.getCreateData error:', err);
          return null;
        }),
        financialApi.getChequeFormData().catch(err => {
          console.warn('financialApi.getChequeFormData error:', err);
          return null;
        }),
      ]);

      let accounts = [];
      if (financialRes?.data?.bank_accounts && financialRes.data.bank_accounts.length > 0) {
        accounts = financialRes.data.bank_accounts;
      } else if (bankRes?.bank_accounts && bankRes.bank_accounts.length > 0) {
        accounts = bankRes.bank_accounts;
      } else if (bankRes?.data?.bank_accounts && bankRes.data.bank_accounts.length > 0) {
        accounts = bankRes.data.bank_accounts;
      } else if (bankRes?.banks && bankRes.banks.length > 0) {
        accounts = bankRes.banks;
      } else if (bankRes?.data?.banks && bankRes.data.banks.length > 0) {
        accounts = bankRes.data.banks;
      } else if (bankRes?.data?.accounts && bankRes.data.accounts.length > 0) {
        accounts = bankRes.data.accounts;
      } else if (bankRes?.accounts && bankRes.accounts.length > 0) {
        accounts = bankRes.accounts;
      }

      setBankAccounts(Array.isArray(accounts) ? accounts : []);
      
      if (createRes?.data?.employees) setUsers(createRes.data.employees);
      else if (createRes?.employees) setUsers(createRes.employees);
      
      if (createRes?.data?.categories) setCategories(createRes.data.categories);
      else if (createRes?.categories) setCategories(createRes.categories);
    } catch (error) {
      console.error('Error fetching dependencies', error);
    }
  };

  useEffect(() => {
    fetchPettyCashes();
    fetchDependencies();
  }, []);

  const handleCreate = async (values) => {
    setActionLoading(true);
    try {
      await pettyCashApi.create({
        ...values,
        initial_amount: Number(values.initial_amount),
      });
      message.success('تنخواه جدید با موفقیت ایجاد شد');
      setIsCreateModalVisible(false);
      createForm.resetFields();
      setCreateAmount(null);
      fetchPettyCashes();
    } catch (error) {
      message.error(extractApiUserMessage(error) || 'خطا در ایجاد تنخواه');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSpend = async (values) => {
    if (!selectedPettyCash) return;
    setActionLoading(true);
    try {
      let formattedPurchaseDate = undefined;
      if (values.purchase_date) {
        if (values.purchase_date?.toDate && typeof values.purchase_date.toDate === 'function') {
          const d = values.purchase_date.toDate();
          const y = d.getFullYear();
          const m = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          formattedPurchaseDate = `${y}-${m}-${day}`;
        } else {
          let str = typeof values.purchase_date === 'string'
            ? values.purchase_date
            : (typeof values.purchase_date?.format === 'function' ? values.purchase_date.format('YYYY/MM/DD') : String(values.purchase_date));
          str = str.replace(/[۰-۹]/g, d => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d))
                   .replace(/[٠-٩]/g, d => '٠١٢٣٤٥٦٧٨٩'.indexOf(d));
          formattedPurchaseDate = toGregorian(str, 'YYYY-MM-DD') || str;
        }
      }

      await pettyCashApi.spend(selectedPettyCash.id, {
        ...values,
        price: Number(values.price),
        purchase_date: formattedPurchaseDate,
      });
      message.success('هزینه از محل تنخواه با موفقیت ثبت شد');
      setIsSpendModalVisible(false);
      spendForm.resetFields();
      setSpendAmount(null);
      fetchPettyCashes();
    } catch (error) {
      message.error(extractApiUserMessage(error) || 'خطا در ثبت هزینه');
    } finally {
      setActionLoading(false);
    }
  };

  const fetchCategories = async () => {
    setCategoryLoading(true);
    try {
      const res = await expenseCategoriesApi.getCreateData().catch(() => null);
      if (res?.data?.categories) {
        setCategories(res.data.categories);
      } else {
        const expRes = await expensesApi.getCreateData().catch(() => null);
        if (expRes?.data?.categories) setCategories(expRes.data.categories);
        else if (expRes?.categories) setCategories(expRes.categories);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    } finally {
      setCategoryLoading(false);
    }
  };

  const handleCreateCategory = async (values) => {
    const catName = values?.category_name?.trim();
    if (!catName) {
      message.warning('لطفاً نام دسته‌بندی را وارد کنید');
      return;
    }
    setCategorySubmitting(true);
    try {
      const res = await expenseCategoriesApi.create(catName, []);
      message.success('دسته‌بندی با موفقیت افزوده شد');

      let newCat = res?.category || res?.data?.category;
      let updatedList = res?.categories || res?.data?.categories;

      if (Array.isArray(updatedList) && updatedList.length > 0) {
        setCategories(updatedList);
      } else if (newCat) {
        setCategories((prev) => [...prev, newCat]);
      } else {
        await fetchCategories();
      }

      const newId = newCat?.id || (Array.isArray(updatedList) ? updatedList.find((c) => c.category_name === catName)?.id : null);
      if (newId) {
        spendForm.setFieldsValue({ category_id: newId });
      }

      categoryForm.resetFields();
      setIsCategoryModalVisible(false);
    } catch (error) {
      console.error('Error creating category:', error);
      message.error(extractApiUserMessage(error) || 'خطا در ایجاد دسته‌بندی');
    } finally {
      setCategorySubmitting(false);
    }
  };

  const handleQuickAddCategory = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const catName = quickCategoryName.trim();
    if (!catName) {
      message.warning('لطفاً نام دسته‌بندی را بنویسید');
      return;
    }
    setCategorySubmitting(true);
    try {
      const res = await expenseCategoriesApi.create(catName, []);
      message.success('دسته‌بندی جدید با موفقیت ایجاد شد');

      let newCat = res?.category || res?.data?.category;
      let updatedList = res?.categories || res?.data?.categories;

      if (Array.isArray(updatedList) && updatedList.length > 0) {
        setCategories(updatedList);
      } else if (newCat) {
        setCategories((prev) => [...prev, newCat]);
      } else {
        await fetchCategories();
      }

      const newId = newCat?.id || (Array.isArray(updatedList) ? updatedList.find((c) => c.category_name === catName)?.id : null);
      if (newId) {
        spendForm.setFieldsValue({ category_id: newId });
      }

      setQuickCategoryName('');
    } catch (error) {
      console.error('Error quick creating category:', error);
      message.error(extractApiUserMessage(error) || 'خطا در ایجاد دسته‌بندی');
    } finally {
      setCategorySubmitting(false);
    }
  };

  const handleDeleteCategory = async (id) => {
    setDeletingCategoryId(id);
    try {
      await expenseCategoriesApi.delete(id);
      message.success('دسته‌بندی با موفقیت حذف شد');
      setCategories((prev) => prev.filter((c) => c.id !== id));
      if (spendForm.getFieldValue('category_id') === id) {
        spendForm.setFieldsValue({ category_id: undefined });
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      message.error('خطا در حذف دسته‌بندی');
    } finally {
      setDeletingCategoryId(null);
    }
  };

  const handleSettle = (id) => {
    Modal.confirm({
      title: <span style={{ fontFamily: 'CustomFont' }}>تسویه تنخواه</span>,
      content: (
        <span style={{ fontFamily: 'CustomFont' }}>
          آیا از تسویه این دوره تنخواه اطمینان دارید؟
        </span>
      ),
      okText: 'بله، تسویه کن',
      cancelText: 'لغو',
      onOk: async () => {
        try {
          await pettyCashApi.settle(id);
          message.success('تنخواه با موفقیت تسویه شد');
          fetchPettyCashes();
        } catch (error) {
          message.error('خطا در تسویه تنخواه');
        }
      },
    });
  };

  const columns = [
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>عنوان تنخواه</span>,
      dataIndex: 'title',
      key: 'title',
      render: (text) => <span className="font-medium" style={{ fontFamily: 'CustomFont' }}>{text}</span>,
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>مسئول تنخواه</span>,
      dataIndex: 'user',
      key: 'user',
      render: (user) => user ? <span style={{ fontFamily: 'CustomFont' }}>{user.name}</span> : '-',
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>موجودی تنخواه</span>,
      dataIndex: 'current_balance',
      key: 'current_balance',
      render: (balance, record) => (
        <span className="text-blue-600 font-medium" style={{ fontFamily: 'CustomFont' }}>
          {formatMoneyFa(balance !== undefined ? balance : record.initial_amount)}
        </span>
      ),
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>وضعیت</span>,
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'settled' ? 'success' : 'processing'} style={{ fontFamily: 'CustomFont' }}>
          {status === 'settled' ? 'تسویه شده' : 'در جریان'}
        </Tag>
      ),
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>عملیات</span>,
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            style={{ fontFamily: 'CustomFont', backgroundColor: '#1890ff' }}
            onClick={() => {
              setSelectedPettyCash(record);
              setIsSpendModalVisible(true);
            }}
            disabled={record.status === 'settled'}
          >
            ثبت هزینه
          </Button>
          <Button
            size="small"
            style={{ fontFamily: 'CustomFont' }}
            onClick={() => handleSettle(record.id)}
            disabled={record.status === 'settled'}
          >
            تسویه دوره
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" styles={{ body: { padding: 0 } }}>
          {/* Header */}
          <div className="border-b border-gray-200 dark:border-gray-700 p-6 bg-white dark:bg-gray-800 rounded-t-lg">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-orange-50 dark:bg-orange-900/20 flex items-center justify-center">
                  <CreditCardOutlined className="text-orange-600 dark:text-orange-400 text-xl" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-0" style={{ fontFamily: 'CustomFont' }}>
                    مدیریت تنخواه‌گردان
                  </h1>
                  <p className="text-gray-500 dark:text-gray-400 text-sm m-0">
                    افتتاح، ثبت هزینه و تسویه کیف‌پول‌های تنخواه
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Tooltip title="بروزرسانی">
                  <Button icon={<ReloadOutlined />} onClick={fetchPettyCashes} size="middle" loading={loading} />
                </Tooltip>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setCreateAmount(null);
                    createForm.resetFields();
                    if (bankAccounts.length === 0) fetchDependencies();
                    setIsCreateModalVisible(true);
                  }}
                  size="middle"
                  className="bg-green-600 hover:bg-green-700 border-0"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  افتتاح تنخواه جدید
                </Button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 bg-gray-50 dark:bg-gray-900/50">
            {loading ? (
              <TableSkeleton rows={5} columns={5} />
            ) : (
              <Card className="dark:bg-gray-800 border-0 shadow-sm">
                <Table
                  columns={columns}
                  dataSource={pettyCashes}
                  rowKey="id"
                  pagination={false}
                />
              </Card>
            )}
          </div>
        </Card>
      </div>

      {/* Modal افتتاح تنخواه */}
      <Modal
        title={<span style={{ fontFamily: 'CustomFont' }}>افتتاح تنخواه جدید</span>}
        open={isCreateModalVisible}
        onCancel={() => {
          setIsCreateModalVisible(false);
          setCreateAmount(null);
          createForm.resetFields();
        }}
        footer={null}
      >
        <Form form={createForm} layout="vertical" onFinish={handleCreate}>
          <Form.Item name="title" label={<span style={{ fontFamily: 'CustomFont' }}>عنوان تنخواه</span>} rules={[{ required: true, message: 'وارد کردن عنوان تنخواه الزامی است' }]}>
            <Input style={{ fontFamily: 'CustomFont' }} placeholder="مثال: تنخواه دفتر مرکزی، تنخواه نمایشگاه..." />
          </Form.Item>
          <Form.Item name="user_id" label={<span style={{ fontFamily: 'CustomFont' }}>مسئول تنخواه</span>} rules={[{ required: true, message: 'انتخاب مسئول تنخواه الزامی است' }]}>
            <Select showSearch optionFilterProp="children" placeholder="انتخاب مسئول تنخواه..." style={{ fontFamily: 'CustomFont' }}>
              {users.map(u => <Option key={u.id} value={u.id}>{u.name}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item
            name="initial_amount"
            label={<span style={{ fontFamily: 'CustomFont' }}>مبلغ اولیه (تومان)</span>}
            rules={[{ required: true, message: 'وارد کردن مبلغ اولیه تنخواه الزامی است' }]}
          >
            <InputNumber
              className="w-full font-mono text-base font-bold text-emerald-600"
              style={{ fontFamily: 'CustomFont' }}
              placeholder="مبلغ اولیه تنخواه به تومان"
              formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/\$\s?|(,*)/g, '')}
              onChange={(val) => setCreateAmount(val)}
            />
          </Form.Item>
          {createAmount && (
            <div className="text-xs text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 px-3 py-1.5 rounded-lg -mt-3 mb-4 border border-emerald-100 dark:border-emerald-900/50" style={{ fontFamily: 'CustomFont' }}>
              <span className="text-gray-400 ml-1">به حروف:</span>
              <span className="font-bold">{amountToPersianWords(createAmount)}</span>
            </div>
          )}
          <Form.Item
            name="bank_account_id"
            label={<span style={{ fontFamily: 'CustomFont' }}>حساب بانکی (محل تأمین)</span>}
            rules={[{ required: true, message: 'انتخاب حساب بانکی محل تأمین الزامی است' }]}
          >
            <Select
              showSearch
              allowClear
              placeholder="انتخاب حساب بانکی محل تأمین..."
              optionFilterProp="children"
              style={{ fontFamily: 'CustomFont' }}
              filterOption={(input, option) => {
                if (!input) return true;
                const normInput = input.toLowerCase().trim();
                const text = String(option?.children || '').toLowerCase();
                return text.includes(normInput);
              }}
            >
              {bankAccounts.map(b => {
                const label = `${b.bank_name ? `${b.bank_name} - ` : ''}${b.account_name || 'حساب بانکی'}${b.account_number ? ` (${b.account_number})` : ''}`;
                return <Option key={b.id} value={b.id}>{label}</Option>;
              })}
            </Select>
          </Form.Item>
          <Form.Item name="notes" label={<span style={{ fontFamily: 'CustomFont' }}>توضیحات</span>}>
            <Input.TextArea rows={3} style={{ fontFamily: 'CustomFont' }} placeholder="توضیحات اختیاری درباره دوره یا نوع تنخواه..." />
          </Form.Item>
          <Form.Item className="mb-0 text-left">
            <Space>
              <Button onClick={() => {
                setIsCreateModalVisible(false);
                setCreateAmount(null);
                createForm.resetFields();
              }} style={{ fontFamily: 'CustomFont' }}>لغو</Button>
              <Button type="primary" htmlType="submit" loading={actionLoading} style={{ fontFamily: 'CustomFont', backgroundColor: '#1890ff' }}>
                ایجاد تنخواه
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal ثبت هزینه از تنخواه */}
      <Modal
        title={<span style={{ fontFamily: 'CustomFont' }}>ثبت هزینه از تنخواه</span>}
        open={isSpendModalVisible}
        onCancel={() => {
          setIsSpendModalVisible(false);
          setSpendAmount(null);
          spendForm.resetFields();
        }}
        footer={null}
      >
        <Form form={spendForm} layout="vertical" onFinish={handleSpend}>
          <Form.Item name="item_name" label={<span style={{ fontFamily: 'CustomFont' }}>عنوان هزینه</span>} rules={[{ required: true, message: 'وارد کردن عنوان هزینه الزامی است' }]}>
            <Input style={{ fontFamily: 'CustomFont' }} placeholder="مثال: خرید ملزومات مصرفی، هزینه پیک..." />
          </Form.Item>
          <Form.Item
            name="price"
            label={<span style={{ fontFamily: 'CustomFont' }}>مبلغ (تومان)</span>}
            rules={[{ required: true, message: 'وارد کردن مبلغ هزینه الزامی است' }]}
          >
            <InputNumber
              className="w-full font-mono text-base font-bold text-emerald-600"
              style={{ fontFamily: 'CustomFont' }}
              placeholder="مبلغ هزینه به تومان"
              formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/\$\s?|(,*)/g, '')}
              onChange={(val) => setSpendAmount(val)}
            />
          </Form.Item>
          {spendAmount && (
            <div className="text-xs text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 px-3 py-1.5 rounded-lg -mt-3 mb-4 border border-emerald-100 dark:border-emerald-900/50" style={{ fontFamily: 'CustomFont' }}>
              <span className="text-gray-400 ml-1">به حروف:</span>
              <span className="font-bold">{amountToPersianWords(spendAmount)}</span>
            </div>
          )}
          <Form.Item
            name="category_id"
            label={
              <div className="flex items-center justify-between w-full" style={{ width: '100%' }}>
                <span style={{ fontFamily: 'CustomFont' }}>دسته‌بندی</span>
                <Button
                  type="link"
                  size="small"
                  icon={<SettingOutlined />}
                  onClick={() => {
                    categoryForm.resetFields();
                    setIsCategoryModalVisible(true);
                  }}
                  style={{ fontFamily: 'CustomFont', padding: 0 }}
                >
                  مدیریت دسته‌بندی‌ها
                </Button>
              </div>
            }
            rules={[{ required: true, message: 'انتخاب دسته‌بندی الزامی است' }]}
          >
            <Select
              showSearch
              allowClear
              optionFilterProp="children"
              placeholder="انتخاب دسته‌بندی..."
              style={{ fontFamily: 'CustomFont' }}
              dropdownRender={(menu) => (
                <>
                  {menu}
                  <Divider style={{ margin: '8px 0' }} />
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'nowrap',
                      padding: '4px 8px 8px 8px',
                      alignItems: 'center',
                      gap: '8px',
                      direction: 'rtl',
                    }}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    <Input
                      style={{ flex: 1, fontFamily: 'CustomFont' }}
                      placeholder="افزودن دسته‌بندی سریع..."
                      value={quickCategoryName}
                      onChange={(e) => setQuickCategoryName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleQuickAddCategory(e);
                        }
                      }}
                    />
                    <Button
                      type="primary"
                      size="small"
                      icon={<PlusOutlined />}
                      loading={categorySubmitting}
                      onClick={handleQuickAddCategory}
                      style={{ fontFamily: 'CustomFont', backgroundColor: '#1890ff' }}
                    >
                      افزودن
                    </Button>
                  </div>
                </>
              )}
            >
              {categories.map((c) => (
                <Option key={c.id} value={c.id}>
                  {c.category_name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="purchase_date" label={<span style={{ fontFamily: 'CustomFont' }}>تاریخ خرید</span>}>
            <PersianDatePicker placeholder="انتخاب تاریخ خرید" style={{ fontFamily: 'CustomFont' }} />
          </Form.Item>
          <Form.Item name="description" label={<span style={{ fontFamily: 'CustomFont' }}>توضیحات</span>}>
            <Input.TextArea rows={2} style={{ fontFamily: 'CustomFont' }} placeholder="توضیحات اختیاری..." />
          </Form.Item>
          <Form.Item className="mb-0 text-left">
            <Space>
              <Button onClick={() => {
                setIsSpendModalVisible(false);
                setSpendAmount(null);
                spendForm.resetFields();
              }} style={{ fontFamily: 'CustomFont' }}>لغو</Button>
              <Button type="primary" htmlType="submit" loading={actionLoading} style={{ fontFamily: 'CustomFont', backgroundColor: '#1890ff' }}>
                ثبت هزینه
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal مدیریت دسته‌بندی‌های هزینه */}
      <Modal
        title={<span style={{ fontFamily: 'CustomFont' }}>مدیریت دسته‌بندی هزینه‌ها</span>}
        open={isCategoryModalVisible}
        onCancel={() => {
          setIsCategoryModalVisible(false);
          categoryForm.resetFields();
        }}
        footer={null}
        width={550}
        destroyOnClose
      >
        <div className="space-y-4" style={{ fontFamily: 'CustomFont' }}>
          <Card size="small" title={<span style={{ fontFamily: 'CustomFont' }}>افزودن دسته‌بندی جدید</span>}>
            <Form form={categoryForm} layout="vertical" onFinish={handleCreateCategory}>
              <Form.Item
                name="category_name"
                label={<span style={{ fontFamily: 'CustomFont' }}>نام دسته‌بندی</span>}
                rules={[{ required: true, message: 'لطفاً نام دسته‌بندی را وارد کنید' }]}
                className="mb-3"
              >
                <Input placeholder="مثال: ملزومات اداری، پذیرایی، ایاب و ذهاب..." style={{ fontFamily: 'CustomFont' }} />
              </Form.Item>
              <div className="text-left">
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<PlusOutlined />}
                  loading={categorySubmitting}
                  style={{ fontFamily: 'CustomFont', backgroundColor: '#1890ff' }}
                >
                  ثبت و ایجاد دسته‌بندی
                </Button>
              </div>
            </Form>
          </Card>

          <Card
            size="small"
            title={<span style={{ fontFamily: 'CustomFont' }}>لیست دسته‌بندی‌های موجود ({categories.length})</span>}
          >
            <Spin spinning={categoryLoading}>
              {categories.length === 0 ? (
                <div className="text-center py-4 text-gray-400" style={{ fontFamily: 'CustomFont' }}>
                  هیچ دسته‌بندی تعریف نشده است.
                </div>
              ) : (
                <div className="max-h-60 overflow-y-auto space-y-2 pr-1">
                  {categories.map((cat) => (
                    <div
                      key={cat.id}
                      className="flex items-center justify-between p-2 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700"
                    >
                      <span className="font-medium text-gray-800 dark:text-gray-200" style={{ fontFamily: 'CustomFont' }}>
                        {cat.category_name}
                      </span>
                      <Button
                        type="text"
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        loading={deletingCategoryId === cat.id}
                        onClick={() => handleDeleteCategory(cat.id)}
                        title="حذف دسته‌بندی"
                      />
                    </div>
                  ))}
                </div>
              )}
            </Spin>
          </Card>
        </div>
      </Modal>

    </MainLayout>
  );
};

export default PettyCash;
